/* Stage 2 — structural constraint diagnosis.
 *
 * Matching / Dulmage–Mendelsohn on the equations-vs-parameters graph, the (2,3) pebble game on the
 * point-distance subgraph, minimal conflict sets, and the structural-vs-numeric rank cross-check.
 * All of it runs in the core; this module presents the report. */
import { Constraint } from './constraints.js';
import { Param, Point, Primitive, Sketch, fromWire } from './model.js';
import { System } from './system.js';
import { WitnessReport, reportFrom } from './witness.js';
import { core, takeJson, withJson } from './wasm.js';

/** `unsolved`: constraints unsatisfied at a pose that is not stationary — the solve stopped
 *  short, so the core gives no verdict (a conflict is one) and names no culprits.  A status
 *  only: an entity's state is its determination, which holds at a non-solution too. */
export type State = 'well' | 'under' | 'over' | 'conflict' | 'unsolved';

/** Free parameters up to which the automatic numeric cross-check (a dense SVD) runs. */
export const NUMERIC_MAX = 300;

/** A connected component of the constraint graph with its own DOF accounting. */
export interface Component {
  params: Param[];
  constraints: Constraint[];
  structuralRank: number;
  dof: number;
}

export interface Diagnosis {
  nParams: number;                 /* free parameters */
  nEquations: number;              /* hard residual rows */
  structuralRank: number;          /* maximum matching size */
  numericRank: number | null;      /* Jacobian rank at the current configuration */
  numericSkipped: boolean;
  geometricDependency: number;     /* dependencies only the numbers can see */
  shaky: number;                   /* motions blocked at second order: rigid, not DOF */
  over: Constraint[];              /* "remove one of these" */
  implied: Constraint[];           /* implied by a relation-only theorem: consistent, no fix */
  claimsTheorem: Constraint[];     /* `claim ...` statements: hold, and add no rank */
  claimsViolated: Constraint[];    /* do not hold at this solution */
  claimsConsuming: Constraint[];   /* hold only by the pose; enforcing one would take a DOF */
  /* each `k minimizes`/`k maximizes` term: what its stationary curve is, and whether that is what the
   * statement asked for */
  extrema: { constraint: Constraint; verdict: string; asked: boolean }[];
  underParams: Param[];            /* what can move at the configuration diagnosed */
  structuralUnderParams: Param[];
  components: Component[];
  entityState: Map<Primitive, State>;
  rigidClusters: Point[][];        /* from the pebble game on the distance graph */
  redundantDistances: Constraint[];
  violated: Constraint[];
  conflicts: Constraint[] | null;  /* minimal conflict set */
  /* each requirement out of reach of what drives it where the solve stopped, and why in the
   * core's words: the conflict set, where there is one (#510 P7) */
  outOfReach: { constraint: Constraint; text: string }[];
  warnings: string[];
  witness: WitnessReport | null;   /* Stage 4 analysis, on demand */
  dof: number;
  structuralDof: number;
  nRedundant: number;
  structuralNRedundant: number;
  status: State;
  summary: string;
}

export function summary(d: Diagnosis): string {
  return d.summary;
}

export interface DiagnoseOptions {
  system?: System | null;
  /** undefined: run the numeric cross-check only below `numericMax`.  true/false: force it. */
  numeric?: boolean;
  conflicts?: boolean | null;
  witness?: boolean;
  tol?: number;
  numericMax?: number;
  /** Whether the claims about solids are judged (the default).  A page leaves them to a worker
   *  (`core/claims.ts`): they enter nothing else the diagnosis says, and take seconds. */
  solidClaims?: boolean;
  /** Whether the pose is a solve's answer (`SolveResult.settled`, the default).  Not, no claim
   *  is judged on it and a drawing whose rows read as holding is `unsolved` (#449). */
  settled?: boolean;
}

interface RawDiagnosis {
  nParams: number;
  nEquations: number;
  structuralRank: number;
  numericRank: number | null;
  numericSkipped: boolean;
  geometricDependency: number;
  shaky: number;
  over: number[];
  implied: number[];
  claimsTheorem: number[];
  claimsViolated: number[];
  claimsConsuming: number[];
  extrema: [number, string, boolean][];
  underParams: number[];
  structuralUnderParams: number[];
  components: { params: number[]; constraints: number[]; structuralRank: number; dof: number }[];
  entityState: [string, number, State][];
  rigidClusters: number[][];
  redundantDistances: number[];
  violated: number[];
  conflicts: number[] | null;
  outOfReach: [number, string][];
  warnings: string[];
  witness: Parameters<typeof reportFrom>[1] | null;
  dof: number;
  structuralDof: number;
  nRedundant: number;
  structuralNRedundant: number;
  status: State;
  summary: string;
}

function fromRaw(sk: Sketch, d: RawDiagnosis): Diagnosis {
  // a diagnosis run against a System compiled before the last edit can name a constraint the
  // sketch no longer has; drop those rather than handing `undefined` on as a Constraint
  const cons = (v: number[]): Constraint[] =>
    v.map((i) => sk.constraintById(i)).filter((c): c is Constraint => c !== undefined);
  const prm = (v: number[]): Param[] => v.map((i) => sk.paramAt(i));
  const ents = new Map<string, Primitive>();
  // axes and curves included: the core states each one's freedom
  for (const e of sk.allEntities()) ents.set(`${e.kind}:${e.index}`, e);
  return {
    nParams: d.nParams,
    nEquations: d.nEquations,
    structuralRank: d.structuralRank,
    numericRank: d.numericRank,
    numericSkipped: d.numericSkipped,
    geometricDependency: d.geometricDependency,
    shaky: d.shaky,
    over: cons(d.over),
    implied: cons(d.implied),
    claimsTheorem: cons(d.claimsTheorem),
    claimsViolated: cons(d.claimsViolated),
    claimsConsuming: cons(d.claimsConsuming),
    extrema: d.extrema.flatMap(([id, verdict, asked]) => {
      const c = sk.constraintById(id);
      return c ? [{ constraint: c, verdict, asked }] : [];
    }),
    underParams: prm(d.underParams),
    structuralUnderParams: prm(d.structuralUnderParams),
    components: d.components.map((c) => ({
      params: prm(c.params),
      constraints: cons(c.constraints),
      structuralRank: c.structuralRank,
      dof: c.dof,
    })),
    entityState: new Map(d.entityState.map(([k, i, s]) => [ents.get(`${fromWire(k)}:${i}`)!, s])),
    rigidClusters: d.rigidClusters.map((c) => c.map((i) => sk.points[i])),
    redundantDistances: cons(d.redundantDistances),
    violated: cons(d.violated),
    conflicts: d.conflicts === null ? null : cons(d.conflicts),
    outOfReach: d.outOfReach.flatMap(([id, text]) => {
      const c = sk.constraintById(id);
      return c ? [{ constraint: c, text }] : [];
    }),
    warnings: d.warnings,
    witness: d.witness ? reportFrom(sk, d.witness) : null,
    dof: d.dof,
    structuralDof: d.structuralDof,
    nRedundant: d.nRedundant,
    structuralNRedundant: d.structuralNRedundant,
    status: d.status,
    summary: d.summary,
  };
}

/** Structural (and optionally numeric) diagnosis of a sketch at its current configuration.
 *
 *  Pass the `System` you just solved with to avoid a recompile.  `conflicts` left null computes
 *  the minimal conflict set only when some constraint is violated.  `numeric` left undefined runs
 *  the Jacobian rank / null-space cross-check only while the system is small enough for a dense
 *  SVD (`numericMax` free parameters). */
export function diagnose(sketch: Sketch, opts: DiagnoseOptions = {}): Diagnosis {
  const payload = {
    numeric: opts.numeric ?? null,
    conflicts: opts.conflicts ?? null,
    witness: opts.witness ?? false,
    tol: opts.tol ?? 1e-6,
    numericMax: opts.numericMax ?? NUMERIC_MAX,
    solidClaims: opts.solidClaims ?? true,
    settled: opts.settled ?? true,
  };
  const sys = opts.system && opts.system.sketch === sketch ? opts.system : null;
  const raw = withJson(payload, (p, n) => takeJson<RawDiagnosis>(
    sys ? core().gcs_diagnose_with_json(sketch.handle, sys.handle, p, n)
        : core().gcs_diagnose_json(sketch.handle, p, n)));
  return fromRaw(sketch, raw);
}

/** Hard constraints whose residual is not (numerically) zero at the current configuration. */
export function violatedConstraints(sys: System, tol = 1e-6): Constraint[] {
  const ids = takeJson<number[]>(core().gcs_violated_json(sys.sketch.handle, tol)) ?? [];
  return ids.map((i) => sys.sketch.constraintById(i)!);
}

/** Minimal infeasible subset among `candidates` (default: all hard constraints).
 *  "Remove one of these." */
export function minimalConflictSet(sketch: Sketch, candidates: Constraint[] | null = null,
                                   tol = 1e-6): Constraint[] {
  const payload = candidates === null ? null : candidates.map((c) => c.id);
  const ids = withJson(payload, (p, n) =>
    takeJson<number[]>(core().gcs_minimal_conflict_set_json(sketch.handle, p, n, tol))) ?? [];
  return ids.map((i) => sketch.constraintById(i)!);
}

/** (2,3) pebble game on the point-distance graph: the rigid clusters and the redundant Distance
 *  constraints. */
export function distanceRigidity(sketch: Sketch):
    { clusters: Point[][]; redundant: Constraint[] } {
  const d = takeJson<{ clusters: number[][]; redundant: number[] }>(
    core().gcs_distance_rigidity_json(sketch.handle));
  return {
    clusters: d.clusters.map((c) => c.map((i) => sketch.points[i])),
    redundant: d.redundant.map((i) => sketch.constraintById(i)!),
  };
}
